import React from 'react';
import classnames from 'classnames';
import Select from '@gik/ui/Select';
import { gikClassPrefix } from '@gik/core/constants';
import { UIVariant, UISize } from '../types';
import Input, { InputCurrency } from '../Input';
import { Field } from 'react-final-form';
import { FormGroup } from '../Form';
import { FieldState } from 'final-form';
import { formatCurrency, formatCurrencyReact } from '@gik/core/utils/format';
import { CartItem } from '@gik/checkout';
import { Product } from '@gik/core/models/gik/Product';
import i18n from 'i18next';

// TODO: move to checkout package?

export interface ProductPriceProps extends React.HTMLAttributes<HTMLElement> {
  /**
   * A WooCommerce product that will determine what to render
   */
  product: Product;

  /**
   * Style variant to pass to the control component
   */
  variant?: UIVariant;

  /**
   * Size to pass to the control component
   */
  size?: UISize;

  /**
   * passed to the control component
   */
  focus?: boolean;

  /**
   * passed to the control component
   */
  autoFocus?: boolean;

  /**
   * passed to the control component
   */
  disabled?: boolean;

  /**
   * passed to the control component
   */
  name?: string;

  /**
   * passed to the control component
   */
  value?: string;

  /**
   * passed to the control component
   */
  tabIndex?: number;

  /**
   * passed to the form group component
   */
  label?: string;

  /**
   * passed to the form group component
   */
  labelBefore?: string;

  index?: number;

  showCustomError?: boolean;

  /**
   * passed to the control component
   */
  onValueChange?: (value: boolean) => void;
}

interface SelectOption {
  label: string;
  value: string;
}

// block name for this component
const blockName = `${gikClassPrefix}-product-price`;

/**
 * Displays the product price either as a fixed value (plaintext), an input field or a dropdown.
 */
function ProductPrice({
  className,
  product,
  index = 0,
  showCustomError = false,
}: ProductPriceProps): React.ReactElement {
  const blockClasses = classnames([blockName, className || '']);

  function renderInput() {
    const minValue = product.variations[0]?.minValue;
    const maxValue = product.variations[0]?.maxValue;

    return (
      <Field name={`item-${index}`}>
        {({ input, meta: fieldState }) => {
          const cartItem = input.value as CartItem;
          return (
            <>
              <FormGroup fieldState={fieldState as FieldState<object>}>
                <InputCurrency
                  {...input}
                  value={cartItem.price !== undefined ? cartItem.price.toString() : ''}
                  prepend="$"
                  variant="default"
                  sign="none"
                  placeholder="Enter Value"
                  onValueBlur={value => {
                    input.onChange({ ...cartItem, price: cartItem.price ? parseInt(value) : undefined });
                  }}
                  onValueChange={value => {
                    // note: when the value changes we want to temporarily allow float values
                    // only when the input loses focus should the float be formatted to an int
                    input.onChange({ ...cartItem, price: value ? parseFloat(value) : undefined });
                  }}
                />
              </FormGroup>
              {showCustomError && (
                <div className={'custom gik-form-group__error'}>{i18n.t('validation.requiredInput')}</div>
              )}
              <span>
                from {formatCurrency(minValue, { decimals: false })} to {formatCurrency(maxValue, { decimals: false })}
              </span>
            </>
          );
        }}
      </Field>
    );
  }

  function renderSelect() {
    const options: SelectOption[] = product.variations.map(variation => ({
      value: variation.id.toString(),
      label: formatCurrency(variation.price, { decimals: false }),
    }));

    return (
      <Field name={`item-${index}`}>
        {({ input, meta: fieldState }) => {
          const cartItem = input.value as CartItem;
          return (
            <FormGroup fieldState={fieldState as FieldState<object>}>
              <Select
                block
                placeholder="Please select an amount"
                options={options}
                {...input}
                value={cartItem?.variationId?.toString()}
                onChange={(value: string) => {
                  const variationId = parseInt(value);
                  const selectedCartItem: CartItem = {
                    productId: product.id,
                    groupedProductIds: product.groupedProductIds,
                    variationId,
                    price: product.variations.find(variation => variation.id === variationId).price,
                    quantity: 1,
                    name: product.name,
                    checkoutType: product.checkoutType,
                  };
                  input.onChange(selectedCartItem);
                }}
              />
              {showCustomError && (
                <div className={'custom gik-form-group__error'}>{i18n.t('validation.requiredSelect')}</div>
              )}
            </FormGroup>
          );
        }}
      </Field>
    );
  }

  function renderPlainText() {
    return (
      <Field name={`item-${index}`}>
        {({ input, meta: fieldState }) => {
          return (
            <>
              <FormGroup fieldState={fieldState as FieldState<object>}>
                <Input {...input} type="hidden" value={product.price.toString()} />
                <span className={`${blockName}__price`}>
                  {formatCurrencyReact(product.price, { sup: true, decimals: true })}
                </span>
              </FormGroup>
            </>
          );
        }}
      </Field>
    );
  }

  // detect what type of input to render
  let controlComponent: React.ReactNode;
  switch (product.type) {
    case 'input':
      controlComponent = renderInput();
      break;
    case 'select':
      controlComponent = renderSelect();
      break;
    default:
      controlComponent = renderPlainText();
  }

  return <div className={blockClasses}>{controlComponent}</div>;
}

export default ProductPrice;
